import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChipIcon,
  CodeIcon,
  CursorClickIcon,
  DatabaseIcon,
  DocumentTextIcon,
  DownloadIcon,
  FingerPrintIcon,
  PhotographIcon,
  ShareIcon,
  VideoCameraIcon,
  VolumeUpIcon,
} from "@heroicons/react/outline";

import ApiService from "../../services/ApiService";
import UserService from "../../services/UserService";

// Shared gradient used by the grid cards and the detail hero.
export const TYPE_GRADIENT =
  "bg-gradient-to-br from-fairdarkgreen to-fairbrightgreen";

// Work types arrive upper-cased from the API (VIDEO, PICTURE, DAT ...);
// normalise and map to an icon. Falls back to a document icon.
const ICONS = {
  document: DocumentTextIcon,
  picture: PhotographIcon,
  audio: VolumeUpIcon,
  video: VideoCameraIcon,
  interactive: CursorClickIcon,
  software: CodeIcon,
  hardware: ChipIcon,
  data: DatabaseIcon,
  dat: DatabaseIcon,
};

export function iconFor(type) {
  return ICONS[(type || "").toLowerCase()] || DocumentTextIcon;
}

function HeaderAction({ icon: Icon, onClick, label, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className="relative rounded-full bg-white/90 p-1.5 text-fairblue-600 shadow-sm hover:bg-white focus:outline-none focus:ring-2 focus:ring-white"
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </button>
  );
}

/**
 * Compact, polished card for the search / my-works grid view. Shows a
 * gradient type header with the correct icon, title, author, license and a
 * few tag pills, with a hover lift.
 *
 * `owned` (my-works) exposes the owner actions (download original +
 * certificate); otherwise public works show a download (when logged in) and
 * a share action.
 */
function WorkGridCard({ work, owned = false }) {
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const Icon = iconFor(work.type);
  const author = work?.creator?.author?.pseudonym;
  const license = work?.license?.identifier || work.licence;
  const tags = Array.isArray(work.tags) ? work.tags : [];
  // Public works are keyed only by `grid`. A few (dev/test data) lack one and
  // can't be opened/shared until properly registered, so their card is shown
  // but not clickable.
  const href = work.grid ? `/works/${work.grid}` : null;

  const share = async () => {
    const url = `${window.location.origin}/works/${work.grid}`;
    // Prefer the native share sheet (mobile + most desktop browsers) so users
    // can send the work straight to a messenger / mail; fall back to copying.
    if (navigator.share) {
      try {
        await navigator.share({ title: work.title, url });
        return;
      } catch (e) {
        if (e.name === "AbortError") return; // user dismissed the sheet
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  const content = (
    <>
      <div className={`h-28 flex items-center justify-center ${TYPE_GRADIENT}`}>
        <Icon
          className="h-12 w-12 text-white/90 transition-transform duration-200 group-hover:scale-110"
          aria-hidden="true"
        />
      </div>
      <div className="flex-1 p-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-fairblue-500">
          {work.type}
        </span>
        <h3 className="mt-1 font-medium text-gray-900 truncate">{work.title}</h3>
        {author && (
          <p className="mt-1 text-sm text-gray-500 truncate">{author}</p>
        )}
        {license && (
          <p className="mt-2 text-xs text-gray-400 truncate">{license}</p>
        )}
        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, i) => (
              <span
                key={i}
                className="inline-block rounded-full bg-fairblue-100 px-2 py-0.5 text-xs font-medium text-fairblue-700"
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="inline-block px-1 text-xs text-gray-400">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="group relative h-full rounded-xl bg-white ring-1 ring-gray-900/5 shadow-sm overflow-hidden transition duration-200 hover:shadow-lg hover:-translate-y-0.5">
      {href ? (
        <Link
          to={href}
          state={{ background: location }}
          aria-label={work.title}
          className="flex h-full flex-col"
        >
          {content}
        </Link>
      ) : (
        <div className="flex h-full flex-col">{content}</div>
      )}

      {/* action buttons sit on top of the link (siblings, not nested) */}
      <div className="absolute top-2 right-2 z-10 flex gap-1.5">
        {owned && (
          <HeaderAction
            icon={DownloadIcon}
            label="Download work"
            onClick={() => ApiService.downloadWork(work.id, work.filename)}
          />
        )}
        {owned && (
          <HeaderAction
            icon={FingerPrintIcon}
            label="Download certificate"
            onClick={() => ApiService.downloadCertificate(work.id)}
          />
        )}
        {!owned && UserService.isLoggedIn() && (
          <HeaderAction
            icon={DownloadIcon}
            label="Download work"
            onClick={() =>
              ApiService.downloadPublicWork(work.grid, work.filename)
            }
          />
        )}
        {work.grid && (
          <HeaderAction
            icon={ShareIcon}
            label="Copy share link"
            onClick={share}
          >
            {copied && (
              <span className="absolute top-full right-0 mt-1 whitespace-nowrap rounded-md bg-black px-2 py-1 text-xs text-white">
                Copied
              </span>
            )}
          </HeaderAction>
        )}
      </div>
    </div>
  );
}

export default WorkGridCard;
